import { useEffect } from "react";
import { Outlet, Link, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";

import appCssUrl from "../styles.css?url";
import appCssInline from "../styles.css?inline";
import displayFont from "@fontsource-variable/cormorant-garamond/files/cormorant-garamond-latin-wght-normal.woff2?url";
import displayItalicFont from "@fontsource-variable/cormorant-garamond/files/cormorant-garamond-latin-wght-italic.woff2?url";
import bodyFont from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import { initAnalytics } from "@/lib/analytics";
import { initReveal } from "@/lib/reveal";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Página não encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">A página que você procura não existe ou foi movida.</p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Voltar ao início
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1, viewport-fit=cover",
      },
      { name: "theme-color", content: "#0B1020" },
      { name: "author", content: "ZNITH" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "pt_BR" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "SPRINT CODE" },
      { name: "google-site-verification", content: "38XQ7avGPfJWV0eV580yEqUNmhGalMjo-aNL_5DJhM0" },
      { property: "og:title", content: "SPRINT CODE" },
      { name: "twitter:title", content: "SPRINT CODE" },
      { name: "description", content: "Serviço premium e individual de implantação do Claude Code e agentes de IA." },
      {
        property: "og:description",
        content: "Serviço premium e individual de implantação do Claude Code e agentes de IA.",
      },
      {
        name: "twitter:description",
        content: "Serviço premium e individual de implantação do Claude Code e agentes de IA.",
      },
      {
        property: "og:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/fAajhM3sXXbA7Oln6iqVhdTQfC32/social-images/social-1777264918261-Leão_Znith.webp",
      },
      {
        name: "twitter:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/fAajhM3sXXbA7Oln6iqVhdTQfC32/social-images/social-1777264918261-Leão_Znith.webp",
      },
    ],
    links: [
      ...(import.meta.env.DEV ? [{ rel: "stylesheet", href: appCssUrl }] : []),
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: displayFont,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: displayItalicFont,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: bodyFont,
        crossOrigin: "anonymous",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

// O servidor inlina o CSS na tag <style>. No navegador o React nao pode receber "" aqui:
// qualquer nova renderizacao (ex.: clique num botao #ancora) esvaziaria a tag e o site perderia todo o CSS.
// Por isso o navegador reaproveita o texto que ja veio no HTML.
let inlineCssCache: string | undefined;
function getInlineCss(): string {
  if (import.meta.env.SSR) return appCssInline;
  if (inlineCssCache === undefined) {
    inlineCssCache = document.querySelector("style[data-inline-styles]")?.textContent ?? "";
  }
  return inlineCssCache;
}

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="pt-BR" className="dark">
      <head>
        <HeadContent />
        {import.meta.env.PROD ? (
          <style data-inline-styles suppressHydrationWarning dangerouslySetInnerHTML={{ __html: getInlineCss() }} />
        ) : null}
      </head>
      <body>
        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=728950902954412&ev=PageView&noscript=1"
            alt=""
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  useEffect(() => {
    initAnalytics();
    const requestIdle =
      window.requestIdleCallback ??
      ((callback: IdleRequestCallback) => {
        return window.setTimeout(() => callback({ didTimeout: false, timeRemaining: () => 0 }), 800);
      });
    const cancelIdle = window.cancelIdleCallback ?? window.clearTimeout;
    const idleId = requestIdle(() => initReveal(), { timeout: 1400 });
    return () => cancelIdle(idleId);
  }, []);
  return <Outlet />;
}
